import { createVerifier, normalizeUsername } from '@pos/core';
import {
  clearOwnerOnlyData,
  deleteMeta,
  getMeta,
  verifyLocalCredential,
  saveCredential,
  setMeta,
  type DeviceIdentity,
  type LocalCredential,
  type MembershipRow,
} from '@pos/local-db';
import { db, openBusinessDb } from '../lib/db.ts';
import { FunctionError, OfflineError, callFunction, isNetworkError, supabase } from '../lib/supabase.ts';
import { reportError } from '../lib/monitoring.ts';
import { setSyncContext, syncEngine } from './sync.ts';
import { timed } from '../lib/timings.ts';

/** Error de login con un mensaje listo para mostrar. */
export class LoginError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LoginError';
  }
}

const AUTH_STORAGE_KEY = 'pos-saas-auth';

async function startSession(membership: MembershipRow, mode: 'online' | 'offline'): Promise<void> {
  await setMeta(db, 'session', {
    user_id: membership.user_id,
    membership_id: membership.id,
    business_id: membership.business_id,
    mode,
    started_at: new Date().toISOString(),
  });
}

async function rememberCredential(
  kind: LocalCredential['kind'],
  membership: MembershipRow,
  loginId: string,
  secret: string,
): Promise<void> {
  await saveCredential(db, {
    kind,
    business_id: membership.business_id,
    user_id: membership.user_id,
    membership_id: membership.id,
    login_id: loginId,
    verifier: await createVerifier(secret),
  });
}

/** Cierra la sesión del servidor aunque no haya red (supabase-js no la borra si falla el request). */
async function signOutServer(): Promise<void> {
  try {
    const { error } = await supabase.auth.signOut({ scope: 'local' });
    if (error) throw error;
  } catch {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  }
}

function authMessage(error: unknown): string {
  const code = (error as { code?: string })?.code;
  if (code === 'invalid_credentials') return 'Email o contraseña incorrectos.';
  if (code === 'email_not_confirmed') return 'Todavía no confirmaste tu email.';
  if (code === 'over_request_rate_limit') return 'Demasiados intentos. Esperá unos minutos.';
  return error instanceof Error ? error.message : 'No se pudo iniciar sesión.';
}

async function offlineLogin(
  kind: LocalCredential['kind'],
  businessId: string,
  loginId: string,
  secret: string,
): Promise<void> {
  const result = await verifyLocalCredential(db, kind, businessId, loginId, secret);
  if (result.status === 'missing') {
    throw new LoginError(
      'No hay conexión y este usuario nunca entró en este dispositivo. El primer ingreso tiene que ser con internet.',
    );
  }
  if (result.status === 'locked') {
    const minutes = Math.max(1, Math.ceil((new Date(result.lockedUntil).getTime() - Date.now()) / 60_000));
    throw new LoginError(`Demasiados intentos sin conexión. Probá de nuevo en ${minutes} min, o conectate a internet.`);
  }
  if (result.status === 'invalid') {
    const what = kind === 'pin' ? 'Usuario o PIN incorrectos' : 'Email o contraseña incorrectos';
    const left = result.remaining === 1 ? 'Te queda 1 intento' : `Te quedan ${result.remaining} intentos`;
    throw new LoginError(`${what} (validado sin conexión). ${left}.`);
  }
  const membership = await db.memberships.get(result.credential.membership_id);
  if (!membership || !membership.active) throw new LoginError('Tu usuario está desactivado. Hablá con el dueño.');
  await startSession(membership, 'offline');
}

// ---------------------------------------------------------------------------
// Primer uso del dispositivo
// ---------------------------------------------------------------------------

export interface OwnedBusiness {
  membership: MembershipRow;
  businessName: string;
}

/** Login online del dueño para configurar el dispositivo. Devuelve los negocios donde es dueño. */
export async function ownerSignInForSetup(email: string, password: string): Promise<OwnedBusiness[]> {
  if (!navigator.onLine) throw new LoginError('Para configurar este dispositivo necesitás conexión a internet.');
  const { data, error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
  if (error) {
    if (isNetworkError(error)) throw new LoginError('No hay conexión con el servidor. Revisá tu internet.');
    throw new LoginError(authMessage(error));
  }
  const { data: rows, error: rowsError } = await supabase
    .from('memberships')
    .select('*, businesses(name)')
    .eq('user_id', data.user.id)
    .eq('role', 'owner')
    .eq('active', true);
  if (rowsError) throw rowsError;
  const owned = (rows ?? []).map(({ businesses, ...membership }) => ({
    membership: membership as MembershipRow,
    businessName: (businesses as { name: string } | null)?.name ?? 'Negocio',
  }));
  if (owned.length === 0) {
    await signOutServer();
    throw new LoginError('Esta cuenta no es dueña de ningún negocio activo.');
  }
  return owned;
}

/** Termina la configuración: guarda credencial local, abre sesión y baja los datos. */
export async function completeOwnerSetup(membership: MembershipRow, email: string, password: string): Promise<void> {
  await db.memberships.put(membership);
  await rememberCredential('password', membership, email, password);
  await startSession(membership, 'online');
  setSyncContext({ businessId: membership.business_id, userId: membership.user_id });
  await syncEngine.sync();
}

// ---------------------------------------------------------------------------
// Login diario (dispositivo ya habilitado)
// ---------------------------------------------------------------------------

export async function loginOwner(device: DeviceIdentity, email: string, password: string): Promise<void> {
  if (navigator.onLine) {
    try {
      const { data, error } = await timed('Entrar (dueño): validar contraseña en el servidor', () =>
        supabase.auth.signInWithPassword({ email: email.trim(), password }),
      );
      if (error) throw error;
      const { data: membership, error: mError } = await timed('Entrar (dueño): leer membresía', async () => await supabase
        .from('memberships')
        .select('*')
        .eq('business_id', device.business_id)
        .eq('user_id', data.user.id)
        .eq('active', true)
        .maybeSingle());
      if (mError) throw mError;
      if (!membership || membership.role !== 'owner') {
        await signOutServer();
        throw new LoginError('Esta cuenta no es dueña de este negocio.');
      }
      await db.memberships.put(membership as MembershipRow);
      await timed('Entrar (dueño): guardar clave en el dispositivo', () => rememberCredential('password', membership as MembershipRow, email, password));
      await startSession(membership as MembershipRow, 'online');
      return;
    } catch (error) {
      if (error instanceof LoginError) throw error;
      if (!isNetworkError(error)) throw new LoginError(authMessage(error));
      reportOfflineFallback('loginOwner', error);
    }
  }
  await offlineLogin('password', device.business_id, email, password);
}

/**
 * Canjea el token de un solo uso de employee-login por una sesión. Las versiones de
 * Supabase Auth difieren en el tipo esperado ('email' o 'magiclink'): si el primero
 * se rechaza, se prueba el otro.
 */
async function redeemLoginToken(tokenHash: string): Promise<void> {
  const first = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'email' });
  if (!first.error) return;
  if (isNetworkError(first.error)) throw first.error;
  const second = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'magiclink' });
  if (second.error) throw first.error;
}

interface EmployeeLoginResponse {
  token_hash: string;
  membership: MembershipRow;
}

export async function loginEmployee(device: DeviceIdentity, username: string, pin: string): Promise<void> {
  const user = normalizeUsername(username);
  if (navigator.onLine) {
    try {
      const res = await timed('Entrar (empleado): validar PIN en el servidor', () =>
        callFunction<EmployeeLoginResponse>('employee-login', {
          business_id: device.business_id,
          device_id: device.id,
          device_secret: device.secret,
          username: user,
          pin,
        }),
      );
      await timed('Entrar (empleado): abrir sesión', () => redeemLoginToken(res.token_hash));
      await db.memberships.put(res.membership);
      await timed('Entrar (empleado): guardar PIN en el dispositivo', () => rememberCredential('pin', res.membership, user, pin));
      await startSession(res.membership, 'online');
      return;
    } catch (error) {
      if (error instanceof FunctionError) throw new LoginError(error.message);
      if (!(error instanceof OfflineError) && !isNetworkError(error)) throw new LoginError(authMessage(error));
      reportOfflineFallback('loginEmployee', error);
    }
  }
  await offlineLogin('pin', device.business_id, user, pin);
}

/**
 * Cambiar de usuario: intenta enviar lo pendiente del usuario actual, cierra la sesión local
 * y la del servidor (para que el próximo usuario no herede permisos ajenos).
 */
export async function logout(): Promise<void> {
  if (navigator.onLine) {
    await timed('Salir: enviar lo pendiente (máx. 5 s)', () =>
      Promise.race([syncEngine.sync(), new Promise((r) => setTimeout(r, 5_000))]).catch(() => undefined),
    );
  }
  setSyncContext({ businessId: null, userId: null });
  // Primero se cierra la sesión del servidor y recién después la local: al borrar la local
  // aparece la pantalla de ingreso, y un login nuevo no puede pisarse con el cierre anterior.
  await timed('Salir: cerrar sesión en el servidor', () => signOutServer());
  // Costos y otros datos del dueño no quedan en el dispositivo para el próximo usuario.
  await timed('Salir: borrar datos del dueño del dispositivo', () => clearOwnerOnlyData(db));
  await deleteMeta(db, 'session');
}

/**
 * La app creía estar online pero una llamada al servidor falló como si no hubiera red y se
 * cayó al modo sin conexión. Se registra para diagnosticar (consola y Sentry si está activo).
 */
function reportOfflineFallback(where: string, error: unknown): void {
  reportError(new Error(`${where}: falló la llamada al servidor con el navegador online; se usa el modo sin conexión`), {
    original: error instanceof Error ? `${error.name}: ${error.message}` : String(error),
  });
}

/**
 * El dueño cambia su contraseña (con conexión). También actualiza el verificador local, para
 * que pueda seguir entrando sin conexión con la nueva.
 */
export async function changeOwnerPassword(membership: MembershipRow, newPassword: string): Promise<void> {
  const { data, error } = await supabase.auth.updateUser({ password: newPassword, data: { must_change_password: false } });
  if (error) throw new LoginError(authMessage(error));
  const email = data.user?.email;
  if (email) await rememberCredential('password', membership, email, newPassword);
}

/**
 * Cambiar de negocio en este dispositivo: cierra la sesión del negocio actual (antes intenta
 * enviar lo pendiente; lo que no se pueda queda guardado en SU base y se envía al volver) y abre
 * la base del otro negocio.
 */
export async function switchBusiness(targetDb: string): Promise<void> {
  if (await getMeta(db, 'session')) await logout();
  else await signOutServer();
  openBusinessDb(targetDb);
}
